import FieldDescription from "../tools/FieldDescription";
import PostalCodeField from "./PostalCodeField";
import { getCityWithPreposition } from "../../utils/functions/getCityWithPreposition";
import { isValidRcs } from "../../utils/functions/validateRCSNumber";
import { fetchCompanyDetailsByRcs } from "../../utils/functions/fetchCompanyDetailsByRcs";
import useValidation from "../../hooks/useValidation";
import legalEntityValidationSchema from "../../utils/validation/legalEntityValidationSchema";
import { useEffect, useState } from "react";
import RcsInput from "./RcsInput";
import CapitalInput from "./CapitalInput";
import { LegalEntityType } from "../../static/types/commonTypes/legalEntityType";
import { CompanyNameType } from "../../static/types/commonTypes/companyNameType";
import '../../styles/animateTexteRCS.css'
import { useDraftManager } from "../../hooks/useDraftManager";

type legalEntityProps = {
    checked: boolean,
    legalEntity: LegalEntityType,
    companyName?: CompanyNameType,
    showRcsCheckBox?: boolean,
    draftFieldName?: string,
    setCompanyName?: (data: Partial<CompanyNameType>) => void,
    societyType?: string | string[],
    changeLegalEntity: (data: Partial<LegalEntityType>) => void,
    onValidationChange: (isValid: boolean) => void
}

export default function LegalEntity({
    checked,
    legalEntity,
    societyType,
    companyName,
    setCompanyName,
    changeLegalEntity,
    onValidationChange,
    draftFieldName,
    showRcsCheckBox = false
}: legalEntityProps) {

    // État local pour gérer les options de forme juridique
    const [legalFormOptions, setLegalFormOptions] = useState<string[]>([]);

    const { errors, isValid } = useValidation(legalEntity, legalEntityValidationSchema);
    const { pushDraftContent } = useDraftManager()

    // useEffect pour initialiser les options de forme juridique
    useEffect(() => {
        const initialOptions = Array.isArray(societyType) ? [...societyType] : societyType ? [societyType] : [];
        setLegalFormOptions(initialOptions);
    }, [societyType]);

    // useEffect pour gérer la validation
    useEffect(() => {
        onValidationChange(isValid)
        if( draftFieldName ){
            pushDraftContent({ [ draftFieldName ]: legalEntity })
        }
    }, [isValid, onValidationChange]);

    // useEffect pour mettre à jour les options quand une nouvelle forme juridique est trouvée
    useEffect(() => {
        if (legalEntity.legalForm && !legalFormOptions.includes(legalEntity.legalForm)) {
            setLegalFormOptions(prev => [legalEntity.legalForm, ...prev]);
        }
    }, [legalEntity.legalForm, legalFormOptions]);

  const handleRcsChange = async (rcsValue: string) => {
  // Toujours mettre à jour le RCS
  changeLegalEntity({ rcs: rcsValue });

  if (isValidRcs(rcsValue)) {
    try {
      const companyDetails = await fetchCompanyDetailsByRcs(rcsValue);
      if (companyDetails) {
        changeLegalEntity({
          ...companyDetails,
          rcs: rcsValue, 
        });

        if (setCompanyName) {
          setCompanyName({
            ...companyName,
            companyName: companyDetails.companyName,
          });
        }
      }
    } catch (error) {
      console.error("Erreur récupération société :", error);
    }
  }
};


    return (
        <div className="mb-3 row">
            <div className="mb-3 row">
                <RcsInput
                    checked={checked}
                    errors={errors}
                    rcs={legalEntity.rcs}
                    onChange={handleRcsChange}
                    disabled={legalEntity.isRcsRegistering}
                />

                <label htmlFor="denomination1" className="col-sm-2 col-form-label required"> 
                    Dénomination <strong className="text-danger">*</strong>  
                </label>

                <div className="col-sm-4">
                    <input
                        className={`description-input form-control ${(checked && errors.companyName) ? 'is-invalid' : ''}`}
                        type="text"
                        name="companyName"
                        id="companyName"
                        value={companyName ? companyName.companyName : legalEntity.companyName}
                        onChange={event => {
                            if (setCompanyName) {
                                setCompanyName({ ...companyName, companyName: event.target.value })
                                changeLegalEntity({ companyName: event.target.value })
                            }
                            else changeLegalEntity({ companyName: event.target.value })
                        }}
                    />
                    {(checked && errors.companyName) && <div className="invalid-feedback"> {errors.companyName} </div>}
                    <FieldDescription description="Nom de la société, comme mentionné dans les statuts." />
                </div>
            </div>

            <div className="mb-3 row">
                <label htmlFor="sociteChoice" className="col-sm-2 col-form-label"> 
                    Forme juridique <strong className="text-danger">*</strong>  
                </label>

                <div className='col-sm-4'>
                    <select
                        className={`form-select ${(checked && errors.legalForm) ? 'is-invalid' : ''}`}
                        name="legalForm"
                        id="legalForm"
                        onChange={event => changeLegalEntity({ legalForm: event.target.value })}
                        value={legalEntity.legalForm || ""}
                    >
                        <option value="">Sélectionnez une forme juridique</option>
                        {
                            legalFormOptions.map((option, index) =>
                                <option key={index} value={option}>
                                    {option}
                                </option>
                            )
                        }
                    </select>
                    {(checked && errors.legalForm) && <div className="invalid-feedback"> {errors.legalForm} </div>}
                </div>

                <CapitalInput
                    inputLabel="Capital"
                    fieldDescription="Montant du capital tel que mentionné dans les statuts. Par exemple : 1535,65"
                    fieldKey="capital"
                    capital={legalEntity.capital}
                    checked={checked}
                    errors={errors}
                    onChange={changeLegalEntity}
                />
            </div>

            <div className="mb-3 row">
                <label htmlFor="address" className="col-sm-2 col-form-label">
                    Adresse <strong className="text-danger">*</strong>  
                </label>

                <div className="col-sm-4">
                    <input
                        className={`form-control ${(checked && errors.address) ? 'is-invalid' : ''}`}
                        type="text"
                        name="address"
                        id="address"
                        value={legalEntity.address}
                        onChange={event => changeLegalEntity({ address: event?.target.value })}
                    />
                    {(checked && errors.address) && <div className="invalid-feedback"> {errors.address} </div>}
                </div>

                <label htmlFor="additionalAddress" className="col-sm-2 col-form-label">Adresse suite </label>

                <div className="col-sm-4">
                    <input
                        className="form-control"
                        type="text"
                        name="additionalAddress"
                        id="additionalAddress"
                        value={legalEntity.additionalAddress}
                        onChange={event => changeLegalEntity({ additionalAddress: event.target.value })}
                    />
                </div>
            </div>

            <div className="mb-3 row">
                <PostalCodeField
                    checked={checked}
                    onChange={changeLegalEntity}
                    postalCode={legalEntity.postalCode}
                    errors={errors}
                    style={{ width: "100%", gap: "4px" }}
                />

                <label htmlFor="city" className="col-sm-2 col-form-label"> 
                    Ville <strong className="text-danger">*</strong>  
                </label>
                <div className="col-sm-4">
                    <select
                        className={`form-control ${(checked && errors.city) ? 'is-invalid' : ''}`}
                        name="city"
                        id="city"
                        onChange={event => changeLegalEntity({ city: event.target.value })}
                        value={legalEntity.city}
                    >
                        {
                            legalEntity.cities?.map((city, key) =>
                                <option key={key} value={city}>
                                    {city}
                                </option>
                            )
                        }
                    </select>
                    {(checked && errors.city) && <div className="invalid-feedback"> {errors.city} </div>}
                </div>
            </div>

            <div className="mb-3 row">
                <label htmlFor="registrationOffice" className="col-sm-2 col-form-label">
                    Greffe <strong className="text-danger">*</strong>  
                </label>

                <div className="col-sm-4">
                    <input
                        className={`form-control ${(checked && errors.registrationOffice) ? 'is-invalid' : ''}`}
                        type="text"
                        id="registrationOffice"
                        value={legalEntity.registrationOffice !== "" ? getCityWithPreposition(legalEntity.registrationOffice) : ""}
                        onChange={event => changeLegalEntity({ registrationOffice: event.target.value })}
                    />
                    {(checked && errors.registrationOffice) && <div className="invalid-feedback"> {errors.registrationOffice} </div>}
                </div>

                {
                    showRcsCheckBox &&
                    <>
                        <input
                            type="checkbox"
                            className="col-sm-1 mt-2 ms-3"
                            name=""
                            id="legal-entity-rcs-checkbox"
                            checked={legalEntity.isRcsRegistering}
                            onChange={event => changeLegalEntity({ isRcsRegistering: event.target.checked })}
                        />

                        <label htmlFor="legal-entity-rcs-checkbox" className="col-sm-5 mt-2">
                            Cocher si le RCS est en cours d'enregistrement
                        </label>
                    </>
                }
            </div>

            <div className="mb-3 row">
                <div className="col text-center mt-3">
                    <p className="zoom-animation">
                        La mise à jour des informations sont quotidiennes, pensez à vérifier l'exactitude.
                    </p>
                </div>
            </div>
        </div>
    )
}